import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Upload, Phone, Mail, GraduationCap, Calendar, FileText, Download, Eye, Trash2, CheckCircle } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import { openBlobInNewTab } from '../utils/fileUtils';
import { useDocumentPreview } from '../components/DocumentPreviewModal';

const ProfilFormateur = ({ user }) => {
  const { previewDocument, DocumentModal } = useDocumentPreview();
  const [profile, setProfile] = useState({
    nom: user.nom || '',
    prenom: user.prenom || '',
    genre: '',
    telephone: user.telephone || '',
    email: user.email || '',
    diplome: '',
    annee_diplome: '',
    specialite: '',
    competences: '',
    photo: '',
    signature: '',
    experience_annees: ''
  });
  const [documents, setDocuments] = useState([]);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const signatureRef = useRef(null);
  const photoInputRef = useRef(null);

  const GENRES = [
    { value: '', label: 'Sélectionner...' },
    { value: 'M.', label: 'Monsieur' },
    { value: 'Mme', label: 'Madame' }
  ];

  const DOCUMENT_TYPES = [
    { type: 'permis_cotier', label: 'Permis Côtier' },
    { type: 'carte_pro', label: 'Carte Professionnelle' },
    { type: 'crr', label: 'CRR' },
    { type: 'diplome', label: 'Diplôme' },
    { type: 'photo_identite', label: "Photo d'Identité" },
    { type: 'autre', label: 'Autre' }
  ];

  useEffect(() => {
    loadProfile();
    loadDocuments();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await axios.get(`/formateurs/${user.user_id}`);
      if (response.data.profile) {
        setProfile({ ...profile, ...response.data.profile });
      }
    } catch (error) {
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await axios.get(`/documents/${user.user_id}`);
      setDocuments(response.data);
    } catch (error) {
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`/formateurs/${user.user_id}/profile`, profile);
      toast.success('Profil mis à jour avec succès');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      const newProfile = { ...profile, photo: reader.result };
      setProfile(newProfile);
      try {
        await axios.put(`/formateurs/${user.user_id}/profile`, newProfile);
        toast.success('Photo mise à jour');
      } catch (error) {
        toast.error('Erreur lors de la mise à jour');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;

    // Supprimer l'ancien document du même type (éviter le cumul)
    const existingDoc = documents.find(d => d.document_type === docType);
    if (existingDoc) {
      try {
        await axios.delete(`/documents/${existingDoc.document_id}`);
      } catch (error) {
      }
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', user.user_id);
    formData.append('document_type', docType);

    try {
      await axios.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploadé');
      loadDocuments();
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    }
  };

  const handleViewDocument = async (documentId, filename) => {
    await previewDocument(documentId, filename);
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`/documents/${documentId}`);
      toast.success('Document supprimé');
      loadDocuments();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDownloadDocument = async (documentId, filename) => {
    try {
      toast.info('Téléchargement...');
      const response = await axios.get(`/documents/download/${documentId}`);
      const doc = response.data;
      if (doc.file_data) {
        const byteCharacters = atob(doc.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: doc.content_type || 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename || doc.filename || 'document');
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleSaveSignature = async () => {
    if (signatureRef.current.isEmpty()) {
      toast.error('Veuillez dessiner votre signature');
      return;
    }

    const signatureData = signatureRef.current.toDataURL();
    const newProfile = { ...profile, signature: signatureData };
    setProfile(newProfile);
    
    try {
      await axios.put(`/formateurs/${user.user_id}/profile`, newProfile);
      setShowSignaturePad(false);
      toast.success('Signature enregistrée');
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  return (
    <div className="space-y-6" data-testid="profil-formateur-page">
      {/* Header Bandeau Bleu */}
      <div className="bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] rounded-xl p-6 text-white shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          {/* Photo */}
          <div className="flex-shrink-0 relative">
            <div className="w-24 h-32 bg-white/20 border-4 border-white/40 rounded-lg overflow-hidden" data-testid="formateur-photo-preview">
              {profile.photo ? (
                <img src={profile.photo} alt="Photo" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="h-12 w-12 text-white/60" />
                </div>
              )}
            </div>
            <input 
              ref={photoInputRef}
              type="file" 
              accept="image/*" 
              onChange={handlePhotoUpload} 
              className="hidden" 
              data-testid="formateur-photo-input" 
            />
            <button 
              onClick={() => photoInputRef.current?.click()}
              className="absolute -bottom-2 -right-2 bg-white text-blue-600 p-2 rounded-full shadow-lg hover:bg-blue-50 transition-colors"
              title="Changer la photo"
            >
              <Upload className="h-4 w-4" />
            </button>
          </div>
          
          {/* Infos principales */}
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-white/80 text-sm">{profile.genre || ''}</span>
              <h1 className="text-2xl md:text-3xl font-bold">
                {profile.prenom || user.prenom} {profile.nom || user.nom}
              </h1>
            </div>
            
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/90 mb-3">
              {profile.telephone && (
                <span className="flex items-center gap-1">
                  <Phone className="h-4 w-4" />
                  {profile.telephone}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Mail className="h-4 w-4" />
                {profile.email || user.email}
              </span>
            </div>
            
            <div className="bg-white/10 rounded-lg px-4 py-3">
              <p className="font-semibold flex items-center gap-2 mb-2">
                <GraduationCap className="h-5 w-5" />
                {profile.diplome || 'Diplôme non renseigné'}
                {profile.annee_diplome && ` (${profile.annee_diplome})`}
              </p>
              {profile.specialite && (
                <p className="text-sm text-white/80">Spécialité: {profile.specialite}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Informations */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Informations</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="genre">Genre</Label>
            <select
              id="genre"
              value={profile.genre || ''}
              onChange={(e) => setProfile({ ...profile, genre: e.target.value })}
              className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md"
              data-testid="formateur-select-genre"
            >
              {GENRES.map(g => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="prenom">Prénom</Label>
            <Input id="prenom" value={profile.prenom} onChange={(e) => setProfile({ ...profile, prenom: e.target.value })} data-testid="formateur-input-prenom" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="nom">Nom</Label>
            <Input id="nom" value={profile.nom} onChange={(e) => setProfile({ ...profile, nom: e.target.value })} data-testid="formateur-input-nom" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="telephone">Téléphone</Label>
            <Input id="telephone" value={profile.telephone} onChange={(e) => setProfile({ ...profile, telephone: e.target.value })} data-testid="formateur-input-telephone" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={profile.email} disabled className="bg-slate-50" data-testid="formateur-input-email" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="experience_annees">Nombre d'années d'expérience</Label>
            <Input id="experience_annees" type="number" min="0" value={profile.experience_annees} onChange={(e) => setProfile({ ...profile, experience_annees: e.target.value })} placeholder="Ex: 5" data-testid="formateur-experience-annees" />
          </div>
        </div>
      </div>

      {/* Diplôme et Spécialité */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Diplôme et Spécialité</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="diplome">Diplôme</Label>
            <Input id="diplome" value={profile.diplome} onChange={(e) => setProfile({ ...profile, diplome: e.target.value })} placeholder="BPJEPS, DEJEPS..." data-testid="formateur-input-diplome" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="annee_diplome">Année du Diplôme</Label>
            <Input id="annee_diplome" type="number" value={profile.annee_diplome} onChange={(e) => setProfile({ ...profile, annee_diplome: e.target.value })} placeholder="2020" data-testid="formateur-input-annee" />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="specialite">Spécialité</Label>
            <Input id="specialite" value={profile.specialite} onChange={(e) => setProfile({ ...profile, specialite: e.target.value })} placeholder="Motonautisme, Jet-ski..." data-testid="formateur-input-specialite" />
          </div>
        </div>
      </div>

      {/* Mes Compétences */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Mes Compétences</h2>
        <div className="space-y-2">
          <Label htmlFor="competences">Décrivez vos compétences et expériences</Label>
          <textarea
            id="competences"
            value={profile.competences || ''}
            onChange={(e) => setProfile({ ...profile, competences: e.target.value })}
            placeholder="Ex: 5 ans d'expérience en formation jet-ski, encadrement de groupes, pédagogie adaptée aux débutants..."
            className="w-full min-h-[120px] px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
            data-testid="formateur-input-competences"
          />
        </div>
      </div>

      {/* Documents */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="h-5 w-5 text-blue-500" />
          <h2 className="text-xl font-display font-semibold text-[#0F172A]">Mes Documents</h2>
        </div>
        <div className="overflow-hidden border border-slate-200 rounded-lg" data-testid="formateur-documents-table">
          {/* Desktop Table */}
          <table className="w-full hidden md:table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">Type</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">Fichier</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {DOCUMENT_TYPES.map((docType) => {
                const existingDoc = documents.find(d => d.document_type === docType.type);
                return (
                  <tr key={docType.type} className={existingDoc ? 'bg-green-50/30' : ''} data-testid={`formateur-doc-row-${docType.type}`}>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-slate-700">{docType.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      {existingDoc ? (
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm text-slate-700 truncate max-w-[200px]">{existingDoc.filename}</p>
                            {existingDoc.uploaded_at && (
                              <p className="text-xs text-slate-400">{new Date(existingDoc.uploaded_at).toLocaleDateString('fr-FR')}</p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400 italic">Non televerse</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {existingDoc && (
                          <>
                            <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => handleViewDocument(existingDoc.document_id, existingDoc.filename)} title="Voir" data-testid={`formateur-view-${docType.type}`}>
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownloadDocument(existingDoc.document_id, existingDoc.filename)} title="Telecharger" data-testid={`formateur-download-${docType.type}`}>
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDeleteDocument(existingDoc.document_id)} title="Supprimer" data-testid={`formateur-delete-${docType.type}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        <div className="relative">
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleDocumentUpload(e, docType.type)}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            data-testid={`formateur-doc-input-${docType.type}`}
                          />
                          <Button size="sm" className={`h-7 pointer-events-none ${existingDoc ? 'bg-slate-500 hover:bg-slate-600' : 'bg-[#8DC63F] hover:bg-[#7db336]'} text-white`} data-testid={`formateur-doc-button-${docType.type}`}>
                            <Upload className="h-3.5 w-3.5 mr-1" />
                            {existingDoc ? 'Remplacer' : 'Televerser'}
                          </Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y divide-slate-100">
            {DOCUMENT_TYPES.map((docType) => {
              const existingDoc = documents.find(d => d.document_type === docType.type);
              return (
                <div key={docType.type} className={`p-4 ${existingDoc ? 'bg-green-50/30' : ''}`} data-testid={`formateur-doc-card-${docType.type}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700">{docType.label}</span>
                    {existingDoc ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <span className="text-xs text-slate-400 italic">Manquant</span>
                    )}
                  </div>
                  {existingDoc && (
                    <p className="text-xs text-slate-500 truncate mb-2">{existingDoc.filename}</p>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    {existingDoc && (
                      <>
                        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => handleViewDocument(existingDoc.document_id, existingDoc.filename)}>
                          <Eye className="h-3.5 w-3.5 mr-1" /> Voir
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => handleDownloadDocument(existingDoc.document_id, existingDoc.filename)}>
                          <Download className="h-3.5 w-3.5 mr-1" /> Telecharger
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDeleteDocument(existingDoc.document_id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                    <div className="relative">
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleDocumentUpload(e, docType.type)} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                      <Button size="sm" className={`h-8 text-xs pointer-events-none ${existingDoc ? 'bg-slate-500 hover:bg-slate-600' : 'bg-[#8DC63F] hover:bg-[#7db336]'} text-white`}>
                        <Upload className="h-3.5 w-3.5 mr-1" />
                        {existingDoc ? 'Remplacer' : 'Televerser'}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {/* Documents supplémentaires (types hors liste) */}
        {documents.filter(d => !DOCUMENT_TYPES.some(dt => dt.type === d.document_type)).length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-semibold text-slate-600 mb-2">Autres documents</h3>
            <div className="space-y-2">
              {documents.filter(d => !DOCUMENT_TYPES.some(dt => dt.type === d.document_type)).map(doc => (
                <div key={doc.document_id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 text-[#8DC63F] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{doc.filename}</p>
                      <p className="text-xs text-slate-500 capitalize">{doc.document_type?.replace(/_/g, ' ')}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 self-end sm:self-auto flex-shrink-0">
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => handleViewDocument(doc.document_id, doc.filename)}><Eye className="h-3.5 w-3.5" /></Button>
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownloadDocument(doc.document_id, doc.filename)}><Download className="h-3.5 w-3.5" /></Button>
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDeleteDocument(doc.document_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Signature */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Signature</h2>
        <p className="text-sm text-slate-600 mb-4">Votre signature sera utilisée sur les feuilles de présence et documents officiels.</p>
        {!showSignaturePad && profile.signature ? (
          <div>
            <div className="border border-slate-200 rounded-lg p-4 inline-block bg-white" data-testid="formateur-saved-signature">
              <img src={profile.signature} alt="Signature" className="h-24" />
            </div>
            <div className="mt-4">
              <Button onClick={() => setShowSignaturePad(true)} variant="outline" data-testid="formateur-change-signature-button">
                Modifier la signature
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="border-2 border-slate-200 rounded-lg overflow-hidden" data-testid="formateur-signature-canvas">
              <SignatureCanvas
                ref={signatureRef}
                canvasProps={{ className: 'w-full h-48 bg-white' }}
              />
            </div>
            <div className="flex space-x-3 mt-4">
              <Button onClick={handleSaveSignature} className="bg-[#8DC63F] hover:bg-[#7db336]" data-testid="formateur-save-signature-button">
                Enregistrer
              </Button>
              <Button onClick={() => signatureRef.current.clear()} variant="outline" data-testid="formateur-clear-signature-button">
                Effacer
              </Button>
              {profile.signature && (
                <Button onClick={() => setShowSignaturePad(false)} variant="outline" data-testid="formateur-cancel-signature-button">
                  Annuler
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSave} className="bg-[#8DC63F] hover:bg-[#7db336] text-white px-8" data-testid="formateur-save-profile-button">
          Enregistrer les modifications
        </Button>
      </div>
      <DocumentModal />
    </div>
  );
};

export default ProfilFormateur;
